import { Stack, Theme } from '@mui/joy';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { MouseEvent } from 'react';

import { Button } from 'core/components/Button';
import { BellNotificationSurface } from 'notifications/enums';
import { IBellNotification, IBellNotificationCta } from 'notifications/types';
import { getNotificationActions } from 'notifications/utils/notification';
import { Color, Direction, ModeType, Variant } from 'theme/enums';

interface IProps {
  notification: IBellNotification;
  onAction: (notification: IBellNotification, cta: IBellNotificationCta) => void;
  surface: BellNotificationSurface;
}

interface IButtonStyle {
  color: Color;
  variant: Variant;
}

// The dark primary button text (#1976D2) is too dim on the dark tints of the rows and the cards.
const PRIMARY_DARK_SX = (theme: Theme) => ({
  [theme.getColorSchemeSelector(ModeType.DARK)]: {
    color: 'primary.700',
    borderColor: 'primary.600',
  },
});

// The drawer row's soft buttons; the toast follows the Alert design (outlined lead, plain rest).
const getButtonStyle = (
  cta: IBellNotificationCta,
  surface: BellNotificationSurface
): IButtonStyle => {
  if (BellNotificationSurface.TOAST === surface) {
    return {
      color: Color.PRIMARY,
      variant: cta.primary ? Variant.OUTLINED : Variant.PLAIN,
    };
  }

  return {
    color: cta.primary ? Color.PRIMARY : Color.NEUTRAL,
    variant: Variant.SOFT,
  };
};

export const NotificationActions = ({ notification, onAction, surface }: IProps) => {
  const actions = getNotificationActions(notification);

  // The host surface opens the notification on a click anywhere else.
  const handleOnAction = (event: MouseEvent, cta: IBellNotificationCta) => {
    event.stopPropagation();
    onAction(notification, cta);
  };

  if (!actions.length) {
    return null;
  }

  return (
    // spacing 0: the theme's default Stack spacing is a margin; it offsets wrapped buttons.
    <Stack direction={Direction.ROW} spacing={0} sx={{ flexWrap: 'wrap', gap: 1, marginTop: 0.5 }}>
      {actions.map((cta) => {
        const buttonStyle = getButtonStyle(cta, surface);

        return (
          <Button
            key={cta.key}
            onClick={(event: MouseEvent) => handleOnAction(event, cta)}
            text={cta.label}
            startDecorator={cta.external ? <ArrowSquareOut /> : undefined}
            sx={Color.PRIMARY === buttonStyle.color ? PRIMARY_DARK_SX : undefined}
            {...buttonStyle}
          />
        );
      })}
    </Stack>
  );
};
